import { useEffect, useRef, useState } from 'react';
import type { ApiLanguage } from '@luacentrix/shared';
import { Icon } from '@luacentrix/icons';
import { IconButton } from '@luacentrix/ui';
import { useApp } from '../app-state';
import { THEME_ORDER, type ThemeName } from '../data/theme';
import { UI_STYLE_ORDER, type GlassCapability, type UiStyleName } from '../data/ui-style';

const THEME_LABELS: Record<ApiLanguage, Record<ThemeName, string>> = {
  en: { dark: 'Dark', light: 'Light' },
  th: { dark: 'โหมดมืด', light: 'โหมดสว่าง' },
};

const THEME_HINTS: Record<ApiLanguage, Record<ThemeName, string>> = {
  en: {
    dark: 'Low light, high contrast surfaces.',
    light: 'Daylight surfaces with deep text.',
  },
  th: {
    dark: 'พื้นผิวแสงน้อย คอนทราสต์สูง',
    light: 'พื้นผิวเวลากลางวัน ตัวอักษรเข้ม',
  },
};

function themeIcon(name: ThemeName): 'moon' | 'sun' {
  return name === 'dark' ? 'moon' : 'sun';
}

const STYLE_LABELS: Record<ApiLanguage, Record<UiStyleName, string>> = {
  en: { legacy: 'Legacy UI', glass: 'Liquid Glass' },
  th: { legacy: 'UI แบบเดิม', glass: 'Liquid Glass' },
};

function styleHint(style: UiStyleName, language: ApiLanguage, glass: GlassCapability): string {
  if (language === 'th') {
    if (style === 'legacy') return 'พื้นทึบปกติ อ่านง่าย ใช้ได้กับทุกเบราว์เซอร์';
    return glass.refraction
      ? 'พื้นผิวกระจกจริง แสงหักเหตามเบื้องหลัง'
      : 'พื้นผิวกระจกแบบเบลอ เพราะเบราว์เซอร์นี้ไม่รองรับการหักเหแสง';
  }
  if (style === 'legacy') return 'Solid surfaces. Works in every browser.';
  return glass.refraction
    ? 'Refractive glass surfaces that bend what sits behind them.'
    : 'Frosted glass surfaces. This browser cannot refract, so edges and blur carry the effect.';
}

function styleIcon(style: UiStyleName): 'stack' | 'droplet' {
  return style === 'legacy' ? 'stack' : 'droplet';
}

export function ThemePicker() {
  const { theme, setTheme, language, uiStyle, setUiStyle, glass } = useApp();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open === false) return;
    const onPointerDown = (event: PointerEvent) => {
      const node = wrapRef.current;
      if (node !== null && !node.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        const items = panelRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]');
        if (items === undefined || items.length === 0) return;
        event.preventDefault();
        const index = Array.prototype.indexOf.call(items, document.activeElement);
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const next = (index + step + items.length) % items.length;
        items[next]?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (open === false) return;
    panelRef.current?.querySelector<HTMLButtonElement>('[role="menuitemradio"]')?.focus();
  }, [open]);

  const labels = THEME_LABELS[language];
  const hints = THEME_HINTS[language];

  return (
    <div className="theme-picker" ref={wrapRef}>
      <IconButton
        ref={triggerRef}
        name={themeIcon(theme)}
        label={language === 'th' ? 'เปลี่ยนธีม' : 'Change theme'}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(current => !current)}
      />
      {open ? (
        <div className="theme-picker__panel" role="menu" aria-label="Theme" ref={panelRef}>
          <p className="theme-picker__title">{language === 'th' ? 'ธีมของหน้าจอ' : 'Appearance'}</p>
          {THEME_ORDER.map(name => (
            <button
              aria-checked={theme === name}
              className="theme-picker__option"
              key={name}
              onClick={() => {
                setTheme(name);
                setOpen(false);
                triggerRef.current?.focus();
              }}
              role="menuitemradio"
              type="button"
            >
              <span className="theme-picker__glyph" data-tone={name}>
                <Icon name={themeIcon(name)} size="sm" />
              </span>
              <span className="theme-picker__text">
                <span className="theme-picker__name">{labels[name]}</span>
                <span className="theme-picker__hint">{hints[name]}</span>
              </span>
              {theme === name ? (
                <span className="theme-picker__current">
                  <Icon name="check" size="sm" />
                </span>
              ) : null}
            </button>
          ))}
          <p className="theme-picker__title">
            {language === 'th' ? 'รูปแบบพื้นผิว' : 'Interface style'}
          </p>
          {UI_STYLE_ORDER.map(name => (
            <button
              aria-checked={uiStyle === name}
              className="theme-picker__option"
              key={name}
              onClick={() => {
                setUiStyle(name);
                setOpen(false);
                triggerRef.current?.focus();
              }}
              role="menuitemradio"
              type="button"
            >
              <span className="theme-picker__glyph" data-tone={name}>
                <Icon name={styleIcon(name)} size="sm" />
              </span>
              <span className="theme-picker__text">
                <span className="theme-picker__name">{STYLE_LABELS[language][name]}</span>
                <span className="theme-picker__hint">{styleHint(name, language, glass)}</span>
              </span>
              {uiStyle === name ? (
                <span className="theme-picker__current">
                  <Icon name="check" size="sm" />
                </span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
